import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button } from './Button.js';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * 兜底错误边界：任何渲染期异常（例如后端数据形状与预期不符）都变成可见的错误框，
 * 而不是白屏 —— 这属于"不允许静默失败"的最后一道防线。
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ error });
    console.error('[CrossMatch] 渲染异常已拦截：', error, errorInfo.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="error-box" role="alert">
        <div className="error-title">页面渲染出错（不是网络问题）</div>
        <p className="error-msg wrap-anywhere">{error.message}</p>
        <p className="error-meta">
          异常已被拦截，避免整页白屏。若与后端返回的数据结构有关，请把这句话连同控制台堆栈反馈给开发者。
        </p>
        <div className="row">
          <Button variant="primary" size="sm" onClick={() => this.setState({ error: null })}>
            重试渲染
          </Button>
          <Button variant="ghost" size="sm" onClick={() => window.location.reload()}>
            刷新页面
          </Button>
        </div>
      </div>
    );
  }
}